import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { 
  Loader2, Sparkles, Target, CheckCircle2, AlertCircle, 
  Image, FileText, Phone, Dog, Baby, Home, ArrowRight,
  Bot, Search, Globe, Zap, TrendingUp, RefreshCw,
  ChevronRight, AlertTriangle, Crown, Star, ExternalLink
} from 'lucide-react';

import { API } from '@/utils/api';

// AI Engine icons and colors
const AI_ENGINES = [
  { id: 'chatgpt', name: 'ChatGPT', icon: Bot, color: 'bg-green-500', textColor: 'text-green-600' },
  { id: 'google', name: 'Google AI', icon: Search, color: 'bg-blue-500', textColor: 'text-blue-600' },
  { id: 'gemini', name: 'Gemini', icon: Sparkles, color: 'bg-purple-500', textColor: 'text-purple-600' },
  { id: 'perplexity', name: 'Perplexity', icon: Globe, color: 'bg-cyan-500', textColor: 'text-cyan-600' },
];

export default function AIScan() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [kennels, setKennels] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [litters, setLitters] = useState([]);
  const [studs, setStuds] = useState([]);
  
  // AI Scan Results
  const [overallScore, setOverallScore] = useState(0);
  const [previousScore, setPreviousScore] = useState(null);
  const [engineScores, setEngineScores] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [showScoreAnimation, setShowScoreAnimation] = useState(false);
  
  // External website scan
  const [scanMode, setScanMode] = useState('apisai'); // 'apisai' or 'external'
  const [externalUrl, setExternalUrl] = useState('');
  const [externalScanResult, setExternalScanResult] = useState(null);
  const [scanningExternal, setScanningExternal] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      fetchDataAndScan();
    }
  }, [user, authLoading, navigate]);

  const fetchDataAndScan = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [kennelsRes, littersRes, studsRes] = await Promise.all([
        axios.get(`${API}/kennels`, { headers }),
        axios.get(`${API}/litters`, { headers }),
        axios.get(`${API}/studs`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setKennels(kennelsRes.data);
      setLitters(littersRes.data);
      setStuds(studsRes.data || []);
      
      let dogsData = [];
      if (kennelsRes.data.length > 0) {
        const dogsRes = await axios.get(`${API}/kennels/${kennelsRes.data[0].id}/dogs`, { headers }).catch(() => ({ data: [] }));
        dogsData = dogsRes.data || [];
        setDogs(dogsData);
      }
      
      // Perform AI Scan
      performAIScan(kennelsRes.data[0], dogsData, littersRes.data, studsRes.data || []);
      
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const performAIScan = (kennel, dogsList, littersList, studsList) => {
    const blockersList = [];
    const completedList = [];
    let totalPoints = 0;
    let earnedPoints = 0;
    
    // === KENNEL SCORING (40 points) ===
    totalPoints += 10;
    if (kennel) {
      earnedPoints += 10;
      completedList.push({ 
        id: 'kennel-exists', 
        text: 'Profil hodowli utworzony',
        points: 10
      });
      
      // Kennel photo (10 points)
      totalPoints += 10;
      const hasKennelPhoto = kennel.cover_image_url || (kennel.gallery_image_urls?.length > 0);
      if (hasKennelPhoto) {
        earnedPoints += 10;
        completedList.push({ id: 'kennel-photo', text: 'Zdjęcie hodowli', points: 10 });
      } else {
        blockersList.push({
          id: 'add-kennel-photo',
          title: 'Dodaj zdjęcie hodowli',
          impact: 15,
          reason: 'AI preferuje profile ze zdjęciami. Wizualne treści budują zaufanie i zwiększają CTR o 40%.',
          path: `/panel/profil`,
          icon: Image,
          category: 'critical'
        });
      }
      
      // Kennel description (10 points)
      totalPoints += 10;
      const descLength = kennel.description?.length || 0;
      if (descLength >= 100) {
        earnedPoints += 10;
        completedList.push({ id: 'kennel-desc', text: 'Opis hodowli (min. 100 znaków)', points: 10 });
      } else {
        blockersList.push({
          id: 'add-kennel-desc',
          title: 'Uzupełnij opis hodowli',
          impact: 12,
          reason: `Masz tylko ${descLength}/100 znaków. Dłuższe opisy pomagają AI zrozumieć Twoją ofertę i lepiej Cię rekomendować.`,
          path: `/panel/profil`,
          icon: FileText,
          category: 'high'
        });
      }
      
      // Contact info (10 points)
      totalPoints += 10;
      const hasContact = kennel.phone || kennel.email || kennel.messenger_url;
      if (hasContact) {
        earnedPoints += 10;
        completedList.push({ id: 'kennel-contact', text: 'Dane kontaktowe', points: 10 });
      } else {
        blockersList.push({
          id: 'add-contact',
          title: 'Dodaj dane kontaktowe',
          impact: 10,
          reason: 'Bez danych kontaktowych AI nie może polecić Twojego profilu osobom szukającym hodowli.',
          path: `/panel/profil`,
          icon: Phone,
          category: 'high'
        });
      }
    } else {
      blockersList.push({
        id: 'create-kennel',
        title: 'Utwórz profil hodowli',
        impact: 25,
        reason: 'Bez profilu hodowli nie jesteś widoczny dla AI. To podstawa Twojej obecności online.',
        path: '/panel/profil',
        icon: Home,
        category: 'critical'
      });
    }
    
    // === DOGS SCORING (30 points) ===
    totalPoints += 15;
    const allAnimals = [...dogsList, ...studsList];
    if (allAnimals.length > 0) {
      earnedPoints += 15;
      completedList.push({ id: 'has-dogs', text: `${allAnimals.length} psów dodanych`, points: 15 });
      
      // Dogs photos
      totalPoints += 15;
      const dogsWithPhotos = allAnimals.filter(d => d.cover_image_url || d.gallery_image_urls?.length > 0);
      if (dogsWithPhotos.length === allAnimals.length) {
        earnedPoints += 15;
        completedList.push({ id: 'dogs-photos', text: 'Wszystkie psy mają zdjęcia', points: 15 });
      } else {
        const missing = allAnimals.length - dogsWithPhotos.length;
        const dogWithoutPhoto = allAnimals.find(d => !d.cover_image_url && !d.gallery_image_urls?.length);
        if (dogWithoutPhoto) {
          blockersList.push({
            id: 'add-dog-photo',
            title: `Dodaj zdjęcie: ${dogWithoutPhoto.name}`,
            impact: 8,
            reason: `${missing} psów nie ma zdjęć. Profile ze zdjęciami mają 3x więcej zapytań.`,
            path: `/panel/pies/${dogWithoutPhoto.id}`,
            icon: Image,
            category: 'medium'
          });
        }
      }
    } else if (kennel) {
      blockersList.push({
        id: 'add-dog',
        title: 'Dodaj pierwszego psa',
        impact: 18,
        reason: 'Hodowla bez psów nie jest wiarygodna. Pokaż swoje psy potencjalnym klientom.',
        path: `/panel/hodowla/${kennel.id}/psy`,
        icon: Dog,
        category: 'high'
      });
    }
    
    // === LITTERS SCORING (30 points) ===
    totalPoints += 20;
    if (littersList.length > 0) {
      earnedPoints += 20;
      completedList.push({ id: 'has-litters', text: `${littersList.length} miotów dodanych`, points: 20 });
      
      // Litters photos
      totalPoints += 10;
      const littersWithPhotos = littersList.filter(l => l.cover_image_url || l.gallery_image_urls?.length > 0);
      if (littersWithPhotos.length === littersList.length) {
        earnedPoints += 10;
        completedList.push({ id: 'litters-photos', text: 'Wszystkie mioty mają zdjęcia', points: 10 });
      } else {
        const litterWithoutPhoto = littersList.find(l => !l.cover_image_url);
        if (litterWithoutPhoto) {
          blockersList.push({
            id: 'add-litter-photo',
            title: `Dodaj zdjęcie miotu: ${litterWithoutPhoto.name}`,
            impact: 12,
            reason: 'Mioty ze zdjęciami sprzedają się 2x szybciej. Zdjęcia budują emocjonalną więź.',
            path: `/wizard/miot/${litterWithoutPhoto.id}`,
            icon: Image,
            category: 'high'
          });
        }
      }
    } else {
      blockersList.push({
        id: 'add-litter',
        title: 'Dodaj miot',
        impact: 20,
        reason: 'Mioty są głównym źródłem leadów! To najszybszy sposób na przyciągnięcie klientów.',
        path: '/wizard/miot',
        icon: Baby,
        category: 'critical'
      });
    }
    
    // Calculate overall score
    const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
    
    // Calculate engine-specific scores (simulated variation)
    const engines = AI_ENGINES.map(engine => ({
      ...engine,
      score: Math.max(0, Math.min(100, score + Math.floor(Math.random() * 15) - 7))
    }));
    
    // Sort blockers by impact
    const sortedBlockers = blockersList.sort((a, b) => b.impact - a.impact);
    
    // Check for score change
    const savedScore = localStorage.getItem('apisai_last_score');
    if (savedScore) {
      const lastScore = parseInt(savedScore);
      if (score > lastScore) {
        setPreviousScore(lastScore);
        setShowScoreAnimation(true);
        setTimeout(() => setShowScoreAnimation(false), 3000);
      }
    }
    localStorage.setItem('apisai_last_score', score.toString());
    
    setOverallScore(score);
    setEngineScores(engines);
    setBlockers(sortedBlockers.slice(0, 5)); // Show top 5 blockers
    setCompletedItems(completedList);
  };

  const handleRescan = async () => {
    setScanning(true);
    toast.info('Skanowanie profilu...');
    
    // Simulate scan delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    await fetchDataAndScan();
    setScanning(false);
    toast.success('Skanowanie zakończone!');
  };

  // Scan external website - uses real backend API
  const handleExternalScan = async () => {
    if (!externalUrl.trim()) {
      toast.error('Podaj adres strony');
      return;
    }
    
    // Validate URL
    let url = externalUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    try {
      new URL(url);
    } catch {
      toast.error('Podaj poprawny adres strony');
      return;
    }
    
    setScanningExternal(true);
    toast.info('Analizuję stronę...');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Zaloguj się, aby skanować strony');
        setScanningExternal(false);
        return;
      }
      
      const response = await axios.post(`${API}/websites/scan`, 
        { url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { scan_results } = response.data;
      
      // Transform backend response to frontend format
      const results = {
        url: url,
        score: scan_results.overall_score,
        checks: scan_results.categories.map(cat => ({
          name: cat.name,
          passed: cat.score >= 70,
          tip: cat.description,
          score: cat.score
        })),
        recommendations: scan_results.recommendations || [],
        recommendation: scan_results.recommendations?.length > 0 
          ? scan_results.recommendations[0]
          : 'Zainstaluj nasz widget AI, aby zwiększyć widoczność Twojej strony w ChatGPT i Google AI.',
      };
      
      setExternalScanResult(results);
      toast.success('Analiza zakończona!');
    } catch (error) {
      console.error('External scan error:', error);
      const errorMsg = error.response?.data?.detail || 'Błąd podczas skanowania strony';
      toast.error(errorMsg);
    } finally {
      setScanningExternal(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-[#F47A3B]';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-[#F47A3B]';
    return 'bg-red-500';
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'critical':
        return <Badge className="bg-red-100 text-red-700 text-xs">Krytyczne</Badge>;
      case 'high':
        return <Badge className="bg-orange-100 text-orange-700 text-xs">Wysoki priorytet</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Średni</Badge>;
      default:
        return <Badge className="bg-zinc-100 text-zinc-700 text-xs">Niski</Badge>;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EE] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#F47A3B] animate-spin mx-auto mb-4" />
          <p className="text-[var(--zinc-500, #71717a)]">Analizuję Twój profil...</p>
        </div>
      </div>
    );
  }

  return (
    <PanelLayout title={t('aiScan.title')} subtitle={t('aiScan.subtitle')}>
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* ========== SCAN MODE TABS ========== */}
        <div className="bg-white rounded-xl border border-zinc-200 p-2 flex gap-2">
          <button
            onClick={() => setScanMode('apisai')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              scanMode === 'apisai' 
                ? 'bg-[#0B1F3B] text-white' 
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <Target className="w-5 h-5" />
            Mój profil ApisAI
          </button>
          <button
            onClick={() => setScanMode('external')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              scanMode === 'external' 
                ? 'bg-[#0B1F3B] text-white' 
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <Globe className="w-5 h-5" />
            Moja własna strona WWW
          </button>
        </div>

        {/* ========== EXTERNAL WEBSITE SCAN ========== */}
        {scanMode === 'external' && (
          <>
            {/* Main Score Card - similar to ApisAI scan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
            >
              {/* Score Header */}
              <div className="p-8 bg-gradient-to-br from-[#0B1F3B] to-[#143A66] text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
                      <Globe className="w-8 h-8" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold">Skanuj swoją stronę WWW</h1>
                      <p className="text-white/70">Sprawdź widoczność dla AI i wyszukiwarek</p>
                    </div>
                  </div>
                  
                  {/* Main Score - only show if we have results */}
                  {externalScanResult && (
                    <div className="text-right">
                      <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative"
                      >
                        <span className={`text-6xl font-bold ${
                          externalScanResult.score >= 80 ? 'text-green-400' : 
                          externalScanResult.score >= 50 ? 'text-[#F47A3B]' : 'text-red-400'
                        }`}>
                          {externalScanResult.score}%
                        </span>
                      </motion.div>
                    </div>
                  )}
                </div>
                
                {/* URL Input */}
                <div className="mt-6 flex gap-3">
                  <Input
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="np. mojahodowla.pl"
                    className="flex-1 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20"
                    onKeyDown={(e) => e.key === 'Enter' && handleExternalScan()}
                  />
                  <Button
                    onClick={handleExternalScan}
                    disabled={scanningExternal}
                    className="h-12 px-8 bg-[#F47A3B] hover:bg-[#E07A0F] text-white font-semibold"
                  >
                    {scanningExternal ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Skanuj
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Results Section */}
              {externalScanResult && (
                <div className="p-6">
                  {/* Scanned URL info */}
                  <div className="flex items-center gap-2 mb-6 p-3 bg-zinc-50 rounded-xl">
                    <ExternalLink className="w-5 h-5 text-zinc-400" />
                    <span className="text-zinc-600">Wynik dla:</span>
                    <span className="font-medium text-zinc-900">{externalScanResult.url}</span>
                  </div>
                  
                  {/* Checks Grid - styled like ApisAI blockers */}
                  <h3 className="text-lg font-semibold text-zinc-900 mb-4">Analiza widoczności</h3>
                  <div className="grid gap-3 mb-6">
                    {externalScanResult.checks.map((check, i) => (
                      <motion.div 
                        key={i} 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className={`p-4 rounded-xl border ${
                          check.passed 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            check.passed ? 'bg-emerald-100' : 'bg-red-100'
                          }`}>
                            {check.passed ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${check.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                              {check.name}
                            </p>
                            {check.tip && (
                              <p className={`text-sm mt-1 ${check.passed ? 'text-emerald-600' : 'text-red-600'}`}>
                                {check.tip}
                              </p>
                            )}
                            {check.score !== undefined && (
                              <div className="mt-2">
                                <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${check.passed ? 'bg-emerald-500' : 'bg-red-500'}`}
                                    style={{ width: `${check.score}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Recommendations */}
                  {externalScanResult.recommendations?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-zinc-900 mb-3">Rekomendacje</h3>
                      <div className="space-y-2">
                        {externalScanResult.recommendations.map((rec, i) => (
                          <div key={i} className="flex items-start gap-2 text-zinc-600">
                            <span className="text-[#F47A3B]">•</span>
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Widget CTA */}
                  <div className="p-5 bg-gradient-to-r from-[#F47A3B]/10 to-[#F47A3B]/5 rounded-xl border border-[#F47A3B]/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F47A3B]/20 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-[#F47A3B]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-zinc-900 mb-1">Zwiększ widoczność z naszym widgetem!</p>
                        <p className="text-sm text-zinc-600 mb-3">
                          {externalScanResult.recommendation || 'Zainstaluj nasz widget AI, aby zwiększyć widoczność Twojej strony w ChatGPT i Google AI.'}
                        </p>
                        <Link to="/panel/moja-strona">
                          <Button className="bg-[#F47A3B] hover:bg-[#E07A0F] text-white">
                            Pobierz widget
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Empty state */}
              {!externalScanResult && !scanningExternal && (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-10 h-10 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-medium text-zinc-900 mb-2">Sprawdź swoją stronę</h3>
                  <p className="text-zinc-500 max-w-md mx-auto">
                    Wpisz adres swojej strony internetowej powyżej i kliknij &quot;Skanuj&quot;, 
                    aby sprawdzić jak jest widoczna dla AI i wyszukiwarek.
                  </p>
                </div>
              )}
              
              {/* Loading state */}
              {scanningExternal && (
                <div className="p-12 text-center">
                  <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#F47A3B] animate-spin" />
                  <h3 className="text-lg font-medium text-zinc-900 mb-2">Analizuję stronę...</h3>
                  <p className="text-zinc-500">Sprawdzam meta tagi, schema.org, zdjęcia i więcej</p>
                </div>
              )}
            </motion.div>
          </>
        )}

        {/* ========== APISAI PROFILE SCAN ========== */}
        {scanMode === 'apisai' && (
          <>
        {/* ========== MAIN SCORE CARD ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
        >
          {/* Score Header */}
          <div className="p-8 bg-gradient-to-br from-[#0B1F3B] to-[#143A66] text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Target className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">AI Visibility Score</h1>
                  <p className="text-white/70">Jak dobrze AI widzi Twoją hodowlę</p>
                </div>
              </div>
              
              {/* Main Score */}
              <div className="text-right">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={overallScore}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    className="relative"
                  >
                    <span className={`text-6xl font-bold ${
                      overallScore >= 80 ? 'text-green-400' : 
                      overallScore >= 50 ? 'text-[#F47A3B]' : 'text-red-400'
                    }`}>
                      {overallScore}%
                    </span>
                    
                    {/* Score increase animation */}
                    {showScoreAnimation && previousScore !== null && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="absolute -top-2 -right-16 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold"
                      >
                        +{overallScore - previousScore}%
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
                
                <Button 
                  onClick={handleRescan}
                  disabled={scanning}
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-white/70 hover:text-white hover:bg-white/10"
                >
                  {scanning ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Skanuj ponownie
                </Button>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="mt-6">
              <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${overallScore}%` }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className={`h-full rounded-full ${getScoreBgColor(overallScore)}`}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-white/50">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
          
          {/* Engine Breakdown */}
          <div className="p-6 border-t border-[rgba(17,24,39,0.10)]">
            <h3 className="text-sm font-medium text-[var(--zinc-500, #71717a)] mb-4 uppercase tracking-wide">
              Widoczność w poszczególnych AI
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {engineScores.map((engine, idx) => {
                const Icon = engine.icon;
                return (
                  <motion.div
                    key={engine.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * idx }}
                    className="p-4 rounded-xl bg-[#F7F3EE] hover:bg-[#F47A3B]/5 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg ${engine.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-zinc-900">{engine.name}</span>
                    </div>
                    <div className="flex items-end gap-2">
                      <span className={`text-2xl font-bold ${getScoreColor(engine.score)}`}>
                        {engine.score}%
                      </span>
                      {engine.score >= overallScore && (
                        <TrendingUp className="w-4 h-4 text-green-500 mb-1" />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ========== BLOCKERS / RECOMMENDATIONS ========== */}
        {blockers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-[rgba(17,24,39,0.10)] bg-gradient-to-r from-red-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">Blokery widoczności</h2>
                  <p className="text-[var(--zinc-500, #71717a)] text-sm">Napraw te problemy, aby zwiększyć widoczność</p>
                </div>
              </div>
            </div>
            
            <div className="divide-y divide-[rgba(17,24,39,0.05)]">
              {blockers.map((blocker, idx) => {
                const Icon = blocker.icon;
                return (
                  <motion.div
                    key={blocker.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + idx * 0.1 }}
                    className="p-6 hover:bg-[#F7F3EE]/50 transition-colors group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F7F3EE] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#F47A3B]" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-zinc-900">{blocker.title}</h3>
                          {getCategoryBadge(blocker.category)}
                          <Badge className="bg-green-100 text-green-700 text-xs ml-auto">
                            +{blocker.impact}% widoczności
                          </Badge>
                        </div>
                        <p className="text-[var(--zinc-500, #71717a)] text-sm mb-4">{blocker.reason}</p>
                        
                        <Link to={blocker.path}>
                          <Button 
                            className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Napraw teraz
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ========== COMPLETED ITEMS ========== */}
        {completedItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-[rgba(17,24,39,0.10)] bg-gradient-to-r from-green-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">Zrealizowane elementy</h2>
                  <p className="text-[var(--zinc-500, #71717a)] text-sm">{completedItems.length} elementów zaliczonych</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {completedItems.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + idx * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-green-50"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 text-sm flex-1">{item.text}</span>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      +{item.points} pkt
                    </Badge>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ========== PERFECT SCORE STATE ========== */}
        {blockers.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white text-center"
          >
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Gratulacje! 🎉</h2>
            <p className="text-white/80 max-w-md mx-auto mb-6">
              Twój profil jest w pełni zoptymalizowany pod kątem AI. Jesteś maksymalnie widoczny dla potencjalnych klientów!
            </p>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </motion.div>
        )}

        {/* ========== TIPS CARD ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-[#0B1F3B] rounded-2xl p-6 text-white"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#F47A3B]" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Pro tip: Jak zwiększyć widoczność?</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Dodaj minimum 5 zdjęć wysokiej jakości do każdego profilu</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Pisz szczegółowe opisy używając słów kluczowych (rasa, lokalizacja)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Aktualizuj profile regularnie - AI preferuje świeże treści</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Dodaj dane kontaktowe - zwiększa to wiarygodność profilu</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
        </>
        )}

      </div>
    </PanelLayout>
  );
}
